import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { SharedService } from 'app/services/core/shared.service';
import { AddIndividualApplicationComponent } from './add-individual-application/add-individual-application.component';
import { PersonService } from 'app/services/person/person.service';
import { APPLICATION_STATUS, LEAD_BASED_PAINT } from '@shared/utils/app-static-data';
import { IPersonApplication } from 'app/models/People/person-application';
import { FormControl } from '@angular/forms';
import { CommonDataService } from 'app/services/common/common-data.service';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-individual-application-list',
  templateUrl: './individual-application-list.component.html',
  styleUrls: ['./individual-application-list.component.scss']
})
export class IndividualApplicationListComponent implements OnInit {
  public isMobile: boolean = false;
  public loading: boolean = true;
  public uiData = LEAD_BASED_PAINT;
  public appStatus = APPLICATION_STATUS;
  public pageSize: number = 50;  
  public dataSource: MatTableDataSource<any>;
  public filteredApplications = [];  
  public filterString: string = '';
  public selectedStatus = new FormControl([]);  
  public showPendingLinkApprovals = new FormControl(true);
  public applicationDateFilterStart = new FormControl("");
  public applicationDateFilterEnd = new FormControl("");
  public selectedRenewal = new FormControl("");
  public pendingLinkApprovalApplicationIds = new Set<number>();

  
  @ViewChild('TablePaginator') paginator: MatPaginator;
  @ViewChild('TableSort') sort: MatSort;
  
  individualApplicationTable = {
    icon: 'recent_actors',
    title:'Individual Applications',
    layout:{
      columns:['id', 'firstName', 'lastName', 'status', 'applicationDate', 'lastUpdatedBy', 'lastUpdated','approverEmail','approvalDate'],
      container:[
        {displayName:'Application Id',columnName:'id', type:'string', size:'10'},                
        {displayName:'First Name',columnName:'firstName', type:'string', size:'10'},
        {displayName:'Last Name',columnName:'lastName', type:'string', size:'15'},
        {displayName:'Status',columnName:'status', type:'string', size:'10'},
        {displayName:'Application Date',columnName:'applicationDate', type:'date', size:'10'},
        {displayName:'Last Updated By',columnName:'lastUpdatedBy', type:'string', size:'10'},
        {displayName:'Last Updated',columnName:'lastUpdated', type:'date', size:'10'},
        {displayName:'Approver Email',columnName:'approverEmail', type:'string', size:'10'},
        {displayName:'Approval Date',columnName:'approvalDate', type:'date', size:'10'},          
      ],
      data: []      
    }
  }

  statusColor(status: string): string {
    return this.sharedService.getStatusColor(status);
  }

  constructor(
    private readonly breakpointObserver: BreakpointObserver,
    public sharedService: SharedService,
    public commonService: CommonDataService,
    public personService: PersonService,
    public dialog: MatDialog) 
  {
    this.breakpointObserver.observe([
      Breakpoints.Handset,
      Breakpoints.Tablet,
      Breakpoints.Small,
    ]).subscribe(result => {
      this.isMobile = result.matches;
    });
  }

  ngOnInit(): void {
    this.loadData();
  }

  loadData(){
    this.filterString = this.commonService.individualApplicationFilter.filterString;
    this.selectedStatus = this.commonService.individualApplicationFilter.selectedStatus;    
    this.showPendingLinkApprovals = this.commonService.individualApplicationFilter.showPendingLinkApprovals;
    this.applicationDateFilterStart.setValue(this.commonService.individualApplicationFilter.applicationDateFilterStart === null ? null : new Date(this.commonService.individualApplicationFilter.applicationDateFilterStart).toISOString().substring(0,10));
    this.applicationDateFilterEnd.setValue(this.commonService.individualApplicationFilter.applicationDateFilterEnd === null ? null : new Date(this.commonService.individualApplicationFilter.applicationDateFilterEnd).toISOString().substring(0,10));
    this.selectedRenewal = this.commonService.individualApplicationFilter.selectedRenewal;

    forkJoin({
      applications: this.personService.getAllPersonApplications(),
      pendingLinkApprovalApplicationIds: this.commonService.getPendingLinkApprovalApplicationIds('Individual')
    }).subscribe(
      res=>{
        this.individualApplicationTable.layout.data = res.applications;
        this.filteredApplications = res.applications;
        this.pendingLinkApprovalApplicationIds = new Set(res.pendingLinkApprovalApplicationIds);
        this.filterTable();
        this.loading = false;
      },
      err=>{}
    )
  }

  updateDataSource(){
    this.dataSource = new MatTableDataSource<any>(this.filteredApplications);
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  getStatusTypeValues() {    
    return Object.values(APPLICATION_STATUS);
  }

  filterTable() {
    this.commonService.individualApplicationFilter.selectedStatus = this.selectedStatus;    
    this.commonService.individualApplicationFilter.showPendingLinkApprovals = this.showPendingLinkApprovals;
    this.commonService.individualApplicationFilter.filterString = this.filterString;    
    this.commonService.individualApplicationFilter.applicationDateFilterStart = this.applicationDateFilterStart.value === null ? null : new Date(this.applicationDateFilterStart.value);
    this.commonService.individualApplicationFilter.applicationDateFilterEnd = this.applicationDateFilterEnd.value === null ? null : new Date(this.applicationDateFilterEnd.value);
    this.commonService.individualApplicationFilter.selectedRenewal = this.selectedRenewal;

    this.filteredApplications = this.individualApplicationTable.layout.data.filter(x=>(x.userID + ' ' + x.id + ' ' + x.firstName + ' ' + x.lastName).toLowerCase().includes(this.filterString.trim().toLowerCase()));    
    if (this.selectedStatus.value.length > 0){
      this.filteredApplications = this.filteredApplications.filter(x=>this.selectedStatus.value.includes(x.status));
    }  
    if (this.showPendingLinkApprovals.value === false){
      this.filteredApplications = this.filteredApplications.filter(x => !this.isPendingLinkApproval(x));
    }
    if (this.applicationDateFilterStart.value){
      this.filteredApplications = this.filteredApplications.filter(x => {
        let applicationDate = new Date(x.applicationDate);
        applicationDate.setHours(0, 0, 0, 0);
    
        let filterStartDate = new Date(this.applicationDateFilterStart.value);
        filterStartDate.setHours(0, 0, 0, 0);
    
        return applicationDate >= filterStartDate;
      });      
    }
    if (this.applicationDateFilterEnd.value){
      this.filteredApplications = this.filteredApplications.filter(x => {
        let applicationDate = new Date(x.applicationDate);
        applicationDate.setHours(0, 0, 0, 0);
    
        let filterEndDate = new Date(this.applicationDateFilterEnd.value);
        filterEndDate.setHours(0, 0, 0, 0);
    
        return applicationDate <= filterEndDate;
    });      
    } 
    if (this.selectedRenewal.value !== ''){
      if(this.selectedRenewal.value == 'Near Outstanding'){
        this.filteredApplications = this.filteredApplications.filter(
          x=>x.status !== 'Approved' && 
          x.status !== 'Cancelled' && 
          (x.applicationViableDate !== null && x.applicationViableDate !== '') && 
          (new Date().getTime() - new Date(x.applicationViableDate).getTime()) / (1000 * 60 * 60 * 24) <= 30 && 
          (new Date().getTime() - new Date(x.applicationViableDate).getTime()) / (1000 * 60 * 60 * 24) >= 20);
      }
      else if(this.selectedRenewal.value == 'Expiring'){
        this.filteredApplications = this.filteredApplications.filter(x => {
          let currentDate = new Date();
          let applicationDate = new Date(x.applicationDate);
          let diffInTime = currentDate.getTime() - applicationDate.getTime();
          let diffInDays = diffInTime / (1000 * 3600 * 24);        
          return diffInDays <= 90 && diffInDays >= 60 && x.status !== 'Approved' && x.status !== 'Cancelled';
        });
      }      
      else if(this.selectedRenewal.value == 'Expired'){        
        this.filteredApplications = this.filteredApplications.filter(x => {
          let currentDate = new Date();
          let applicationDate = new Date(x.applicationDate);
          let diffInTime = currentDate.getTime() - applicationDate.getTime();
          let diffInDays = diffInTime / (1000 * 3600 * 24);
          return diffInDays > 90 && x.status !== 'Approved' && x.status !== 'Cancelled';
        });
      } else if(this.selectedRenewal.value == 'Outstanding'){        
        this.filteredApplications = this.filteredApplications.filter(x => {
          return x.status !== 'Approved' && 
          x.status !== 'Cancelled' && 
          (x.applicationViableDate !== null && x.applicationViableDate !== '') &&           
          (new Date().getTime() - new Date(x.applicationViableDate).getTime()) / (1000 * 60 * 60 * 24) > 30;          
        });
      }
    }   
    this.updateDataSource();  
  }

  isPendingLinkApproval(application: any): boolean {
    const isLinkApplication = !application?.certificateType || application.certificateType.toString().trim() === '';
    return isLinkApplication && this.pendingLinkApprovalApplicationIds.has(application.id);
  }

  editIndividualApplication(application: IPersonApplication){    
    const dialogRef = this.dialog.open(AddIndividualApplicationComponent, {
      width: '90%',
      height: '90vh',
      maxHeight: '90vh',
      data: {application: application, edit: true},
      autoFocus: false,});
    dialogRef.afterClosed().subscribe(result => {      
      this.loadData();      
    });
  }

  displayLimit(text: any, limit: string){
    let newText = text;
    if (typeof text === 'string' && text.length > parseInt(limit)){
      newText = newText.substring(0, limit) + "...";
    }    
    return newText;
  }

  getApplicationFilterValues() {
    return [null, 'Expiring', 'Expired', 'Near Outstanding', 'Outstanding'];
  }

  clearFilters(){
    this.filterString = '';
    this.selectedStatus.setValue(['In Progress','Submitted','In Review','Action Needed']);    
    this.showPendingLinkApprovals.setValue(true);
    this.selectedRenewal.setValue('');
    this.applicationDateFilterStart.setValue(null);
    this.applicationDateFilterEnd.setValue(null);
    this.filterTable();
  }
}
